import * as React from "react";
import { Link } from "react-router";
import { WordGlyph } from "./ColoredWord";

import type { Word as FetchedWord } from "@/lib/types";

interface WordProps {
  word: FetchedWord;
  editMode: boolean;
  /** Draw word-link highlights (the "Poveznice" setting). */
  showColors: boolean;
  /** "2:255", for the accessible name (the QCF glyphs are private-use characters). */
  verseKey?: string;
  /** Opens the page's single ayah dialog; `trigger` gets focus back on close. */
  onSelect?: (ayahId: number, trigger: HTMLElement) => void;
}

// Press feedback is a tint, never a scale: the page image must stay still.
const WORD_TARGET_CLASS =
  "rounded-[3px] transition-colors duration-150 hover:bg-gold-100/70 active:bg-gold-200/60";

/** A word on the mushaf page: a plain button (or an editor link in edit mode). */
const Word: React.FC<WordProps> = ({ word, editMode, showColors, verseKey, onSelect }) => {
  if (editMode) {
    return (
      <Link to={`edit?word=${word.id}`} preventScrollReset className={`inline-block ${WORD_TARGET_CLASS}`}>
        <WordGlyph word={word} showColors={showColors} />
      </Link>
    );
  }

  const label = verseKey
    ? word.char_type_name === "end"
      ? `Kraj ajeta ${verseKey}`
      : `Ajet ${verseKey}`
    : undefined;

  return (
    <button
      type="button"
      aria-label={label}
      aria-haspopup="dialog"
      data-ayah-id={word.ayah_id}
      className={WORD_TARGET_CLASS}
      onClick={(event) => onSelect?.(word.ayah_id, event.currentTarget)}
    >
      <WordGlyph word={word} showColors={showColors} />
    </button>
  );
};

export default React.memo(Word);
